import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Button, Popover, Box, Typography } from '@mui/material';
import { useRewardCart } from '@src/hooks/contexts';
import styles from '@src/components/MediaList/RewardButton.module.less';

const STATUS_CONFIG = {
  pending: {
    text: 'SENDING',
    color: 'warning',
    tooltip: 'Gift card has been paid and will be sent soon',
  },
  processing: {
    text: 'SENDING',
    color: 'warning',
    tooltip: 'Gift card is being processed',
  },
  sent: {
    text: 'GIFT SENT',
    color: 'success',
    tooltip: 'Gift card has been sent',
  },
  redeemed: {
    text: 'GIFT REDEEMED',
    color: 'secondary',
    tooltip: 'Gift card has been redeemed',
  },
  failed: {
    text: 'SEND FAILED',
    color: 'error',
    tooltip: 'Gift card send failed',
  },
};

const RewardButton = ({ submission, giftCardSent, rewardEnabled, campaignLink }) => {
  const { isInCart, addToCart, removeFromCart, openCart } = useRewardCart();
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  const approved = isInCart(submission?.campaignSubmissionId);
  const giftOrderStatus = submission?.giftOrderStatus;

  const statusKey = giftCardSent && !giftOrderStatus ? 'sent' : giftOrderStatus;
  const statusInfo = STATUS_CONFIG[statusKey];

  // Status histórico: sempre mostra, mesmo com rewards desabilitado
  if (statusInfo) {
    const statusClass = `${styles.statusButton} ${styles[statusKey] || ''}`;

    return (
      <Tooltip title={statusInfo.tooltip}>
        <Button
          size="small"
          variant="outlined"
          color={statusInfo.color}
          className={statusClass}
          disabled
        >
          {statusInfo.text}
        </Button>
      </Tooltip>
    );
  }

  // Rewards desabilitado e nenhum gift enviado: mostra botão com popover explicativo
  if (!rewardEnabled) {
    const handleOpenPopover = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setPopoverAnchorEl(e.currentTarget);
    };

    const handleClosePopover = (e) => {
      e && e.stopPropagation();
      setPopoverAnchorEl(null);
    };

    const rewardsUrl = campaignLink
      ? `/campaigns/${campaignLink}/edit?view=rewards`
      : null;

    return (
      <>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={styles.statusButton}
          onClick={handleOpenPopover}
        >
          APPROVE GIFT
        </Button>
        <Popover
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ p: 2, maxWidth: 260 }}>
            <Typography variant="body2" gutterBottom>
              Rewards are disabled for this campaign. Enable rewards to approve and send gift cards.
            </Typography>
            {rewardsUrl && (
              <Button
                component="a"
                href={rewardsUrl}
                variant="contained"
                color="primary"
                size="small"
                sx={{ mt: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                Enable Rewards
              </Button>
            )}
          </Box>
        </Popover>
      </>
    );
  }

  // Botão normal: approve / in cart
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (approved) {
      removeFromCart(submission?.campaignSubmissionId);
    } else {
      addToCart(submission);
      setTimeout(() => openCart(), 100);
    }
  };

  const rewardAmount = submission?.campaignRewardAmount || 0;
  const buttonText = approved ? 'IN CART' : 'APPROVE GIFT';
  const buttonColor = approved ? 'error' : 'primary';
  const tooltipText = approved
    ? `Remove from cart ($${rewardAmount})`
    : `Add to cart${rewardAmount > 0 ? ` ($${rewardAmount})` : ''}`;

  return (
    <Tooltip title={tooltipText}>
      <Button
        size="small"
        variant="outlined"
        color={buttonColor}
        className={styles.statusButton}
        onClick={handleToggle}
      >
        {buttonText}
      </Button>
    </Tooltip>
  );
};

RewardButton.propTypes = {
  submission: PropTypes.shape({
    campaignSubmissionId: PropTypes.string.isRequired,
    submitterName: PropTypes.string,
    campaignName: PropTypes.string,
    submitterEmail: PropTypes.string,
    campaignId: PropTypes.number,
    campaignRewardAmount: PropTypes.number,
    giftOrderStatus: PropTypes.oneOf(['pending', 'processing', 'sent', 'redeemed', 'failed']),
  }).isRequired,
  giftCardSent: PropTypes.bool.isRequired,
  rewardEnabled: PropTypes.bool.isRequired,
  campaignLink: PropTypes.string,
  campaignHasRewardConfig: PropTypes.bool,
};

export default RewardButton;



// WEBPACK FOOTER //
// ./src/components/MediaList/RewardButton.jsx